import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';

const PASTEL_PRESETS = [
  '#A8D8EA', // light blue
  '#B5EAD7', // mint green
  '#FFDAC1', // peach
  '#E2B6CF', // light pink/mauve
  '#C7CEEA', // lavender
];

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

interface Props {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}

const ColorPickerInput: FC<Props> = ({ value, onChange, required = false }) => {
  const { formatMessage } = useIntl();
  const [customHex, setCustomHex] = useState(
    value && !PASTEL_PRESETS.includes(value) ? value : ''
  );
  const colorInputRef = useRef<HTMLInputElement>(null);

  // Auto-select first preset if required and no value set
  useEffect(() => {
    if (required && !value) {
      onChange(PASTEL_PRESETS[0]);
    }
  }, [required, value, onChange]);

  const handleCustomChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setCustomHex(v);
      if (HEX_COLOR_REGEX.test(v)) {
        onChange(v);
      }
    },
    [onChange]
  );

  const handleColorPickerChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value.toUpperCase();
      onChange(v);
      setCustomHex(v);
    },
    [onChange]
  );

  const isCustomColor = value && !PASTEL_PRESETS.includes(value);

  return (
    <div>
      <div className="d-flex gap-2 mb-2 align-items-center">
        {PASTEL_PRESETS.map((color) => (
          <span
            key={color}
            role="button"
            tabIndex={0}
            onClick={() => {
              onChange(color);
              setCustomHex('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(color);
                setCustomHex('');
              }
            }}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: color,
              border:
                value === color ? '2px solid #12263F' : '2px solid transparent',
              cursor: 'pointer',
            }}
            title={color}
          />
        ))}
        {!required && (
          <span
            role="button"
            tabIndex={0}
            onClick={() => {
              onChange('');
              setCustomHex('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange('');
                setCustomHex('');
              }
            }}
            className="d-flex align-items-center justify-content-center text-muted"
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: !value ? '2px solid #12263F' : '2px solid #E3EBF6',
              cursor: 'pointer',
              fontSize: '0.7rem',
            }}
            title={formatMessage({
              id: 'app.views.widgets.inputs.color_picker.no_color',
              defaultMessage: 'No color',
            })}
          >
            <i className="fe fe-x" />
          </span>
        )}
        <label
          className="d-flex align-items-center justify-content-center m-0"
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: isCustomColor
              ? value
              : 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
            border: isCustomColor
              ? '2px solid #12263F'
              : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '0.7rem',
            overflow: 'hidden',
          }}
          title={formatMessage({
            id: 'app.views.widgets.inputs.color_picker.custom_color',
            defaultMessage: 'Custom color',
          })}
        >
          <input
            ref={colorInputRef}
            type="color"
            value={value || PASTEL_PRESETS[0]}
            onChange={handleColorPickerChange}
            style={{
              opacity: 0,
              width: 0,
              height: 0,
              position: 'absolute',
            }}
          />
          {!isCustomColor && (
            <i
              className="fe fe-plus"
              style={{
                color: '#fff',
                textShadow: '0 0 2px rgba(0,0,0,0.5)',
              }}
            />
          )}
        </label>
        <Input
          bsSize="sm"
          style={{ width: 100 }}
          placeholder={'#AABBCC'}
          maxLength={7}
          value={customHex}
          onChange={handleCustomChange}
        />
      </div>
    </div>
  );
};

export default React.memo(ColorPickerInput);
